import { countrys, countrysEnum } from "helpers/enums";
import { requiredField, validateLicenseExpirationDate } from "helpers/validators";
import moment from "moment";

const latinLetter = /[A-Za-z]/;

export const documentMasks = {
    [countrysEnum.RussianFederation]: {
        passport: {
            series: "9999",
            number: "999999",
            subdivisionCode: "999-999",
        },
        license: {
            series: "9999",
            number: "999999",
        },
    },
    [countrysEnum.Belarus]: {
        passport: {
            series: "aa",
            number: "9999999",
            subdivisionCode: "999",
        },
        // Note: Belarus license series and number use dynamic masks defined below
    },
    [countrysEnum.Kazakhstan]: {
        passport: {
            series: [/[Nn]/],
            number: "99999999",
        },
        license: {
            series: [latinLetter, latinLetter],
            number: "999999",
        },
    },
    [countrysEnum.Uzbekistan]: {
        passport: {
            series: [latinLetter, latinLetter],
            number: "9999999",
        },
        license: {
            series: [latinLetter, latinLetter],
            number: "999999",
        },
    },
};

export const documentPatterns = {
    [countrysEnum.Kazakhstan]: {
        passport: {
            series: /^N$/,
            number: /^\d{8}$/,
        },
        license: {
            series: /^[A-Z]{2}$/,
            number: /^\d{6}$/,
        },
    },
    [countrysEnum.Uzbekistan]: {
        passport: {
            series: /^[A-Z]{2}$/,
            number: /^\d{7}$/,
        },
        license: {
            series: /^[A-Z]{2}$/,
            number: /^\d{6}$/,
        },
    },
};

export const documentValueFormat = (pattern, message) => (value) => (!value || pattern.test(value) ? "" : message);

// Belarus driver's license series (3 chars: 1 digit + 2 letters OR 4 digits)
export const getLicenseSeriesMask = (val = "") => {
    const cleanVal = (val || "").replace(/_/g, "");
    if (cleanVal.length < 2) {
        return "9***"; // Starts with a digit, followed by anything
    }
    if (/[A-Za-z]/.test(cleanVal[1])) {
        return "9aa"; // Old format: 1 digit + 2 letters (e.g. 2AC)
    }
    return "9999"; // New format: 4 digits (e.g. 1234)
};

// Belarus driver's license number (6 or 7 digits)
export const getLicenseNumberMask = (val = "") => {
    const cleanVal = (val || "").replace(/_/g, "");
    if (cleanVal.length < 6) {
        return "999999";
    }
    return "9999999";
};

const basePassportConfig = {
    issuingCountry: {
        inputProps: { name: "issuingCountry" },
        label: "Страна выдачи",
        validators: [requiredField],
        required: true,
        type: "select",
        options: countrys,
        placeholder: "Выберите страну",
    },
    series: {
        inputProps: { name: "series", placeholder: "9999" },
        label: "Серия паспорта",
        validators: [requiredField],
        required: true,
        type: "customMask",
        mask: documentMasks[countrysEnum.RussianFederation].passport.series,
        maskPlaceholder: "____",
    },
    number: {
        inputProps: { name: "number", placeholder: "999999" },
        label: "Номер паспорта",
        validators: [requiredField],
        required: true,
        type: "customMask",
        mask: documentMasks[countrysEnum.RussianFederation].passport.number,
        maskPlaceholder: "______",
    },
    issuedBy: {
        inputProps: { name: "issuedBy", placeholder: "Например: ГУ МВД России по г. Москве" },
        label: "Кем выдан",
        validators: [requiredField],
        required: true,
    },
    dateOfIssue: {
        inputProps: { name: "dateOfIssue", type: "date" },
        label: "Дата выдачи",
        validators: [requiredField],
        required: true,
        inPortal: true,
        libOptions: {
            maxDate: new Date(),
            isClearable: true,
        },
    },
    subdivisionCode: {
        inputProps: { name: "subdivisionCode", placeholder: "999-999" },
        label: "Код подразделения",
        type: "customMask",
        mask: documentMasks[countrysEnum.RussianFederation].passport.subdivisionCode,
        maskPlaceholder: "___-___",
    },
};

export const getPassportConfig = (issuingCountry) => {
    if (issuingCountry === countrysEnum.Belarus) {
        return {
            ...basePassportConfig,
            series: {
                ...basePassportConfig.series,
                inputProps: { ...basePassportConfig.series.inputProps, placeholder: "AA" },
                required: false,
                validators: [],
                mask: documentMasks[countrysEnum.Belarus].passport.series,
                maskPlaceholder: "__",
            },
            number: {
                ...basePassportConfig.number,
                inputProps: { ...basePassportConfig.number.inputProps, placeholder: "9999999" },
                type: "customMask",
                mask: documentMasks[countrysEnum.Belarus].passport.number,
                maskPlaceholder: "_______",
            },
            subdivisionCode: {
                ...basePassportConfig.subdivisionCode,
                inputProps: { ...basePassportConfig.subdivisionCode.inputProps, placeholder: "999" },
                mask: documentMasks[countrysEnum.Belarus].passport.subdivisionCode,
                maskPlaceholder: "___",
            },
        };
    }
    const countryMasks = documentMasks[issuingCountry]?.passport;
    const countryPatterns = documentPatterns[issuingCountry]?.passport;
    if (countryMasks && countryPatterns) {
        return {
            ...basePassportConfig,
            series: {
                ...basePassportConfig.series,
                inputProps: {
                    ...basePassportConfig.series.inputProps,
                    placeholder: issuingCountry === countrysEnum.Kazakhstan ? "N" : "AA",
                },
                validators: [requiredField, documentValueFormat(countryPatterns.series, "Некорректная серия паспорта")],
                mask: countryMasks.series,
                maskPlaceholder: issuingCountry === countrysEnum.Kazakhstan ? "_" : "__",
            },
            number: {
                ...basePassportConfig.number,
                inputProps: {
                    ...basePassportConfig.number.inputProps,
                    placeholder: issuingCountry === countrysEnum.Kazakhstan ? "99999999" : "9999999",
                },
                validators: [requiredField, documentValueFormat(countryPatterns.number, "Некорректный номер паспорта")],
                mask: countryMasks.number,
                maskPlaceholder: issuingCountry === countrysEnum.Kazakhstan ? "________" : "_______",
            },
            subdivisionCode: {
                ...basePassportConfig.subdivisionCode,
                inputProps: { ...basePassportConfig.subdivisionCode.inputProps, placeholder: "" },
                type: undefined,
                mask: undefined,
                maskPlaceholder: undefined,
            },
        };
    }
    return basePassportConfig;
};

const baseLicenseConfig = {
    issuingCountry: {
        inputProps: { name: "issuingCountry" },
        label: "Страна выдачи",
        validators: [requiredField],
        required: true,
        type: "select",
        options: countrys,
        placeholder: "Выберите страну",
    },
    series: {
        inputProps: { name: "series", placeholder: "9999" },
        label: "Серия",
        validators: [requiredField],
        required: true,
        type: "customMask",
        mask: documentMasks[countrysEnum.RussianFederation].license.series,
        maskPlaceholder: "____",
    },
    number: {
        inputProps: { name: "number", placeholder: "999999" },
        label: "Номер",
        validators: [requiredField],
        required: true,
        type: "customMask",
        mask: documentMasks[countrysEnum.RussianFederation].license.number,
        maskPlaceholder: "______",
    },
    dateOfIssue: {
        inputProps: { name: "dateOfIssue", type: "date" },
        label: "Дата выдачи",
        validators: [requiredField],
        required: true,
        inPortal: true,
        libOptions: {
            maxDate: new Date(),
            isClearable: true,
        },
    },
    expirationDate: {
        inputProps: { name: "expirationDate", type: "date" },
        label: "Дата истечения срока действия",
        validators: [requiredField, validateLicenseExpirationDate],
        required: true,
        inPortal: true,
        libOptions: {
            isClearable: true,
        },
    },
};

export const getLicenseConfig = (issuingCountry, seriesVal, numberVal, dateOfIssueVal) => {
    const minDate = dateOfIssueVal && moment(dateOfIssueVal).isValid() ? moment(dateOfIssueVal).toDate() : undefined;
    const expirationDate = {
        ...baseLicenseConfig.expirationDate,
        validators: [
            requiredField,
            (value, data) => validateLicenseExpirationDate(value, { dateOfIssue: dateOfIssueVal, ...data }),
        ],
        libOptions: {
            ...baseLicenseConfig.expirationDate.libOptions,
            ...(minDate ? { minDate } : {}),
        },
    };

    if (issuingCountry === countrysEnum.Belarus) {
        const licenseSeriesMask = getLicenseSeriesMask(seriesVal);
        const licenseNumberMask = getLicenseNumberMask(numberVal);
        return {
            ...baseLicenseConfig,
            expirationDate,
            series: {
                ...baseLicenseConfig.series,
                inputProps: { ...baseLicenseConfig.series.inputProps, placeholder: "9AA / 9999" },
                type: "customMask",
                mask: licenseSeriesMask,
                maskPlaceholder: licenseSeriesMask.replace(/[*a9]/g, "_"),
            },
            number: {
                ...baseLicenseConfig.number,
                inputProps: { ...baseLicenseConfig.number.inputProps, placeholder: "999999 / 9999999" },
                type: "customMask",
                mask: licenseNumberMask,
                maskPlaceholder: licenseNumberMask.replace(/[*a9]/g, "_"),
            },
        };
    }
    const countryMasks = documentMasks[issuingCountry]?.license;
    const countryPatterns = documentPatterns[issuingCountry]?.license;
    if (countryMasks && countryPatterns) {
        return {
            ...baseLicenseConfig,
            expirationDate,
            series: {
                ...baseLicenseConfig.series,
                inputProps: { ...baseLicenseConfig.series.inputProps, placeholder: "AA" },
                validators: [
                    requiredField,
                    documentValueFormat(countryPatterns.series, "Некорректная серия водительского удостоверения"),
                ],
                mask: countryMasks.series,
                maskPlaceholder: "__",
            },
            number: {
                ...baseLicenseConfig.number,
                validators: [
                    requiredField,
                    documentValueFormat(countryPatterns.number, "Некорректный номер водительского удостоверения"),
                ],
                mask: countryMasks.number,
                maskPlaceholder: "______",
            },
        };
    }
    return {
        ...baseLicenseConfig,
        expirationDate,
    };
};

export { validateLicenseExpirationDate };
